import { Baby, Feather, Droplets, Grab, Play } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";

const BentoCard = ({ className, imgHeight, img, imgClass = "", alt, icon: Icon, title, desc, testid, delay = 0 }) => (
  <Reveal
    delay={delay}
    className={`group relative flex h-full flex-col overflow-hidden rounded-[28px] border border-slate-100 bg-white shadow-[0_20px_50px_-24px_rgba(27,79,160,0.25)] transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_32px_64px_-24px_rgba(240,62,155,0.3)] ${className}`}
  >
    <div
      className={`relative flex items-center justify-center overflow-hidden bg-gradient-to-br from-binky-blue/5 via-white to-binky-pink/5 ${imgHeight}`}
    >
      <img
        src={img}
        alt={alt}
        loading="lazy"
        data-testid={`${testid}-image`}
        className={`transition-transform duration-700 group-hover:scale-[1.05] ${imgClass || "max-h-full w-auto object-contain p-6"}`}
      />
    </div>
    <div className="flex items-start gap-3 p-6">
      <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-binky-pinklight text-binky-pink">
        <Icon className="h-5 w-5" />
      </span>
      <div>
        <h3 className="font-display text-lg font-bold text-slate-900" data-testid={`${testid}-title`}>
          {title}
        </h3>
        <p className="mt-1 text-sm leading-relaxed text-slate-600">{desc}</p>
      </div>
    </div>
  </Reveal>
);

export const PackBento = () => (
  <section id="pack" data-testid="pack-section" className="relative py-24 sm:py-32">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <Reveal className="max-w-2xl">
        <Eyebrow>Everything in One Pack</Eyebrow>
        <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
          Not just a pacifier — <span className="text-binky-pink">a complete comfort kit.</span>
        </h2>
        <p className="mt-4 text-base leading-relaxed text-slate-600 md:text-lg">
          Every Mi Binky pack contains the full system: the comfort pacifier, sterile cotton
          inserts, a 15 ml comfort-liquid bottle, precision tweezers and step-by-step guidance —
          everything you need, in one pack.
        </p>
      </Reveal>

      <div className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-12">
        <BentoCard
          className="md:col-span-7"
          imgHeight="h-[320px] sm:h-[420px]"
          img="/assets/binky-pack-pink.png"
          alt="Mi Binky Comfort System blister pack with pink pacifier"
          icon={Baby}
          title="Mi Binky Comfort Pacifier"
          desc="Soft-touch shield with a protected internal comfort chamber and gentle front ventilation holes."
          testid="bento-pacifier"
        />
        <BentoCard
          className="md:col-span-5"
          imgHeight="h-[220px] sm:h-[280px]"
          img="/assets/cotton-front.png"
          alt="BINKY.LIFE sterile cotton strip packaging"
          icon={Feather}
          title="Absorbent Cotton Inserts"
          desc="Compressed 100% cotton strips — sterile, individually wrapped and sized precisely for the chamber."
          testid="bento-cotton"
          delay={0.1}
        />
        <BentoCard
          className="md:col-span-4"
          imgHeight="h-[240px]"
          img="/assets/bottle-decongestant.png"
          alt="BINKY.LIFE 15 ml comfort drops bottle"
          icon={Droplets}
          title="15 ml Comfort Drops"
          desc="Soft squeeze bottle with a narrow dispensing nozzle and screw closure — one bottle in every pack."
          testid="bento-drops"
          delay={0.05}
        />
        <BentoCard
          className="md:col-span-4"
          imgHeight="h-[240px]"
          img="/assets/binky-pack-pink-06.png"
          imgClass="h-full w-full object-cover object-[50%_88%]"
          alt="Precision tweezers included in the Mi Binky pack"
          icon={Grab}
          title="Precision Tweezers"
          desc="Used cotton is removed hygienically with the supplied tool — never pulled out by hand."
          testid="bento-tweezers"
          delay={0.1}
        />
        <BentoCard
          className="md:col-span-4"
          imgHeight="h-[240px]"
          img="/assets/binky-instructions.png"
          imgClass="h-full w-full object-cover object-[50%_18%]"
          alt="Mi Binky instruction card artwork"
          icon={Play}
          title="Instructions & Video Guide"
          desc="A step-by-step card in every pack, plus a QR-linked video showing exactly how it works."
          testid="bento-guide"
          delay={0.15}
        />
      </div>
    </div>
  </section>
);
